import ContentHubLayout from "@app/layouts/ContentHub/ContentHubLayout";
import ErrorLayout from "@app/layouts/ErrorLayout/ErrorLayout";
import { ContentHubData } from "@app/types/ServerCustomContext";
import AdSettings from "@app/utils/AdSettings";
import { extractUrlRedirect } from "@app/utils/extractUrlRedirect";
import { mapTeaserDataToLatestProps } from "@app/utils/mapTeaserDataToProps";
import MetaData from "@components/Modules/MetaData";
import { mapErrorDataToMetaData } from "@components/Modules/MetaData/mappers";
import PubBaseLayout from "@pub/BaseLayout";
import { configContentHub, configTrackingCode } from "@pub/config";
import { Route } from "@route-types/ContentHub/+types/ContentHub";
import { GlobalBaseData } from "@sphtech/dmg-design-system";
import { ResponseType, TRouteWithRedirect } from "@sphtech/web2-core/ssr";
import { dataLoader } from "@src/app/utils/dataLoader";
import { extractGlobalData } from "@src/app/utils/globalData";
import { useLoaderData, useLocation } from "react-router";

import { fetchContentHubData } from "./ContentHub.server";

export const meta = ({
  loaderData,
  location,
}: {
  loaderData: TRouteWithRedirect<ContentHubData, string>;
  location: { pathname: string };
}) => {
  if (loaderData.type !== ResponseType.SUCCESS) {
    const errorData = mapErrorDataToMetaData(loaderData, location);
    return MetaData(errorData);
  }

  return MetaData(loaderData.payload.metaData);
};

export default function ContentHub() {
  const response: TRouteWithRedirect<ContentHubData, string> = useLoaderData();
  const location = useLocation();

  if (response.type !== ResponseType.SUCCESS) {
    return <ErrorLayout response={response} />;
  }

  const responseData = response.payload;
  const userSubmissionBox =
    responseData.userSubmissionBox?.showOn.topicPaths?.includes(
      location.pathname,
    )
      ? response.payload.userSubmissionBox
      : undefined;

  const displayVariant = responseData.displaySetting?.displayHeadline;

  const contentHubSingleConfig = configContentHub.layout?.stories;
  const contentHubVariantConfig =
    configContentHub.layout?.variant &&
    configContentHub.layout.variant.find(
      (variant) => variant.name === displayVariant,
    );

  const hasLeaderboard = !!configContentHub.configAds?.leaderboardAbove;

  return (
    <PubBaseLayout leaderboard={hasLeaderboard}>
      {/* <MetaData {...responseData.metaData} /> */}
      <AdSettings
        pageType="contenthub"
        sectionName={responseData.name}
        adUnits="lb1,lb2,catfish,prestitial"
        pageTitle={responseData.name}
        currentLocation={location}
        StoryData={{ id: responseData.id }}
      />
      {contentHubSingleConfig?.elements && (
        <ContentHubLayout
          data={{
            ...responseData,
            listedStories:
              responseData.listedStories &&
              responseData.listedStories.map((teaser) =>
                mapTeaserDataToLatestProps(teaser),
              ),
            videoPlaylist: {
              ...responseData.videoPlaylist,
              videoTeaser:
                responseData.videoPlaylist?.videoTeaser &&
                responseData.videoPlaylist.videoTeaser.map((teaser) =>
                  mapTeaserDataToLatestProps(
                    teaser,
                    configTrackingCode.contenthub?.videoplaylist,
                  ),
                ),
            },
            userSubmissionBox,
          }}
          layoutElementConfig={
            contentHubVariantConfig?.elements || contentHubSingleConfig.elements
          }
          ListingLayoutComponent={
            contentHubVariantConfig?.ListingLayoutComponent
          }
        />
      )}
    </PubBaseLayout>
  );
}

export async function loader({
  params,
  context,
  request,
}: Route.LoaderArgs): Promise<
  TRouteWithRedirect<ContentHubData | GlobalBaseData, GlobalBaseData | string>
> {
  if (!params.topicSlug) {
    const globalData = await extractGlobalData();

    return {
      type: ResponseType.CLIENT_ERROR,
      statusCode: 404,
      payload: globalData,
    };
  }

  const data = await fetchContentHubData({
    topicSlug: params.topicSlug,
    previewData: context.previewData,
  });

  if (data.type === ResponseType.CLIENT_ERROR && data.statusCode === 404) {
    const redirectResult = await extractUrlRedirect(request.url);

    if (redirectResult && redirectResult.target) {
      return redirectResult;
    }
  }

  return await dataLoader({
    type: data.type,
    dataSource: data,
  });
}
